// Master-bus spectrum — log-scaled FFT bars painted on a Canvas2D.
//
// Reads the shared AnalyserNode owned by src/strudel/masterAnalyser.ts.
// The analyser is fed by every voice's connect-to-destination, so this
// shows the final summed mix exactly as the speakers hear it.
//
// Animation is on the shared rAF ticker — no per-instance loop. The
// frequency-data buffer is allocated once per mount and reused across
// frames to keep GC pressure flat.

import { memo, useEffect, useRef } from 'react';
import { getMasterAnalyser } from '../../strudel';
import { subscribeTick } from '../../strudel/sharedTicker';

type Props = {
  active: boolean;
  width?: number;
  height?: number;
};

const BAR_COUNT = 28;
// Lower bound of the visible band. Below ~40 Hz is sub content most
// laptop speakers can't reproduce; above the audible top is wasted.
const MIN_HZ = 40;
const MAX_HZ = 16000;

export const Spectrum = memo(SpectrumInner);

function SpectrumInner({ active, width = 180, height = 32 }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Pre-allocated; sized to the analyser the first time we see one.
    // Backed by an explicit ArrayBuffer (not ArrayBufferLike) so the
    // strict overload of getByteFrequencyData accepts it.
    let buf: Uint8Array<ArrayBuffer> | null = null;

    return subscribeTick(() => {
      const analyser = getMasterAnalyser();
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.width;
      const h = canvas.height;

      if (!analyser || !active) {
        ctx.clearRect(0, 0, w, h);
        drawFloor(ctx, w, h, dpr);
        return;
      }

      if (!buf || buf.length !== analyser.frequencyBinCount) {
        buf = new Uint8Array(new ArrayBuffer(analyser.frequencyBinCount));
      }
      analyser.getByteFrequencyData(buf);

      const sampleRate = analyser.context.sampleRate;
      const binHz = sampleRate / 2 / buf.length;

      ctx.clearRect(0, 0, w, h);
      drawFloor(ctx, w, h, dpr);

      const barWidth = (w - (BAR_COUNT - 1) * dpr) / BAR_COUNT;
      const logMin = Math.log10(MIN_HZ);
      const logMax = Math.log10(MAX_HZ);

      for (let i = 0; i < BAR_COUNT; i++) {
        // Each bar covers a log-frequency slice; average the bins inside.
        const f0 = Math.pow(10, logMin + ((logMax - logMin) * i) / BAR_COUNT);
        const f1 = Math.pow(10, logMin + ((logMax - logMin) * (i + 1)) / BAR_COUNT);
        const b0 = Math.max(1, Math.floor(f0 / binHz));
        const b1 = Math.min(buf.length - 1, Math.max(b0 + 1, Math.ceil(f1 / binHz)));
        let acc = 0;
        for (let j = b0; j < b1; j++) acc += buf[j]!;
        const avg = acc / (b1 - b0);
        // Gamma-bend: spectra are spiky, ^1.4 keeps small bins visible
        // without letting big bins clip the chassis.
        const norm = Math.min(1, Math.pow(avg / 255, 1.4) * 1.4);
        const bh = Math.max(1 * dpr, norm * (h - 2 * dpr));
        const x = i * (barWidth + dpr);
        const y = h - bh - dpr;
        ctx.fillStyle = `rgba(253, 224, 71, ${0.35 + norm * 0.55})`;
        ctx.fillRect(x, y, barWidth, bh);
      }
    });
  }, [active]);

  // Resize canvas backing store on mount + on dpr changes. Done outside
  // the rAF loop because it's an allocation event, not a per-frame op.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
  }, [width, height]);

  return (
    <canvas
      ref={canvasRef}
      role="img"
      aria-label="Master spectrum"
      style={{ width, height }}
      className="block"
    />
  );
}

function drawFloor(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  dpr: number,
): void {
  // Faint baseline so the chassis doesn't look broken when idle.
  ctx.fillStyle = 'rgba(63, 63, 70, 0.6)';
  ctx.fillRect(0, h - dpr, w, dpr);
}
